import { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, XCircle, Trash2, Pin, Eye, Clock, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import AdminLayout from '@/components/AdminLayout';

const CAT_LABELS = {
  'general': 'Général', 'cas-cliniques': 'Cas Cliniques', 'lentilles': 'Lentilles',
  'équipement': 'Équipement', 'ortho-k': 'Orthokératologie', 'pediatrie': 'Pédiatrie', 'autre': 'Autre',
};

const ForumModerationPage = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [topicReplies, setTopicReplies] = useState({});
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTopics(); }, []);

  const fetchTopics = async () => {
    try {
      const res = await axios.get(`${API}/forum/topics/all`, { headers });
      setTopics(res.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };

  const moderateTopic = async (id, status) => {
    try {
      await axios.put(`${API}/forum/topics/${id}/moderate`, { status }, { headers });
      toast.success(status === 'approved' ? 'Sujet approuvé' : 'Sujet rejeté');
      fetchTopics();
    } catch { toast.error('Erreur'); }
  };

  const togglePin = async (id, currentPin) => {
    try {
      await axios.put(`${API}/forum/topics/${id}/moderate`, { status: 'approved', pinned: !currentPin }, { headers });
      toast.success(currentPin ? 'Désépinglé' : 'Épinglé');
      fetchTopics();
    } catch { toast.error('Erreur'); }
  };

  const deleteTopic = async (id) => {
    if (!window.confirm('Supprimer ce sujet et toutes ses réponses ?')) return;
    try {
      await axios.delete(`${API}/forum/topics/${id}`, { headers });
      toast.success('Sujet supprimé');
      fetchTopics();
    } catch { toast.error('Erreur'); }
  };

  const loadReplies = async (topicId) => {
    if (expandedTopic === topicId) { setExpandedTopic(null); return; }
    try {
      const res = await axios.get(`${API}/forum/topics/${topicId}/replies/all`, { headers });
      setTopicReplies(prev => ({ ...prev, [topicId]: res.data }));
      setExpandedTopic(topicId);
    } catch { toast.error('Erreur chargement réponses'); }
  };

  const moderateReply = async (replyId, status) => {
    try {
      await axios.put(`${API}/forum/replies/${replyId}/moderate`, { status }, { headers });
      toast.success(status === 'approved' ? 'Réponse approuvée' : 'Réponse rejetée');
      if (expandedTopic) loadReplies(expandedTopic);
    } catch { toast.error('Erreur'); }
  };

  const deleteReply = async (replyId) => {
    try {
      await axios.delete(`${API}/forum/replies/${replyId}`, { headers });
      toast.success('Réponse supprimée');
      if (expandedTopic) loadReplies(expandedTopic);
    } catch { toast.error('Erreur'); }
  };

  const filtered = topics.filter(t => filter === 'all' || t.status === filter);
  const pendingCount = topics.filter(t => t.status === 'pending').length;

  return (
    <AdminLayout>
    <div className="space-y-6" data-testid="forum-moderation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modération Forum</h1>
          <p className="text-sm text-gray-500 mt-1">{topics.length} sujets au total, {pendingCount} en attente</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: `Tous (${topics.length})` },
          { key: 'pending', label: `En attente (${pendingCount})` },
          { key: 'approved', label: 'Approuvés' },
          { key: 'rejected', label: 'Rejetés' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${filter === f.key ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            data-testid={`filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#35AFE4] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">Aucun sujet</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#35AFE4] flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 mt-0.5">
                  {t.author_name?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${t.status === 'pending' ? 'bg-amber-50 text-amber-600' : t.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                      {t.status === 'pending' ? 'En attente' : t.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                    </span>
                    {t.pinned && <Pin size={11} className="text-amber-500 -rotate-45" />}
                    <span className="text-[10px] text-gray-400 px-1.5 py-0.5 bg-gray-50 rounded">{CAT_LABELS[t.category] || t.category}</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">{t.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2">{t.content}</p>
                  <div className="flex items-center gap-3 text-[10px] text-gray-400">
                    <span>{t.author_name}</span>
                    <span className="flex items-center gap-0.5"><Clock size={9} /> {new Date(t.created_at).toLocaleDateString('fr-FR')}</span>
                    <span className="flex items-center gap-0.5"><Eye size={9} /> {t.views}</span>
                    <span className="flex items-center gap-0.5"><MessageSquare size={9} /> {t.replies_count || 0}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {t.status !== 'approved' && (
                    <button onClick={() => moderateTopic(t.id, 'approved')} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-500 transition-colors" title="Approuver" data-testid={`approve-${t.id}`}><CheckCircle size={16} /></button>
                  )}
                  {t.status !== 'rejected' && (
                    <button onClick={() => moderateTopic(t.id, 'rejected')} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition-colors" title="Rejeter"><XCircle size={16} /></button>
                  )}
                  <button onClick={() => togglePin(t.id, t.pinned)} className={`p-1.5 rounded-lg transition-colors ${t.pinned ? 'text-amber-500 bg-amber-50' : 'text-gray-300 hover:bg-gray-50'}`} title={t.pinned ? 'Désépingler' : 'Épingler'}><Pin size={16} className="-rotate-45" /></button>
                  <button onClick={() => deleteTopic(t.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-300 transition-colors" title="Supprimer"><Trash2 size={16} /></button>
                  <button onClick={() => loadReplies(t.id)} className={`p-1.5 rounded-lg hover:bg-gray-50 transition-all ${expandedTopic === t.id ? 'rotate-180' : ''}`}><ChevronDown size={16} className="text-gray-400" /></button>
                </div>
              </div>
              {/* Replies */}
              {expandedTopic === t.id && topicReplies[t.id] && (
                <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-2">
                  <p className="text-xs font-bold text-gray-500 mb-2">{topicReplies[t.id].length} réponse(s)</p>
                  {topicReplies[t.id].map(r => (
                    <div key={r.id} className="bg-white rounded-lg p-3 flex items-start gap-2 border border-gray-100">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-700">{r.author_name}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${r.status === 'pending' ? 'bg-amber-50 text-amber-600' : r.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                            {r.status === 'pending' ? 'Attente' : r.status === 'approved' ? 'OK' : 'Rejeté'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600">{r.content}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {r.status !== 'approved' && <button onClick={() => moderateReply(r.id, 'approved')} className="p-1 rounded hover:bg-emerald-50 text-emerald-400"><CheckCircle size={14} /></button>}
                        {r.status !== 'rejected' && <button onClick={() => moderateReply(r.id, 'rejected')} className="p-1 rounded hover:bg-red-50 text-red-400"><XCircle size={14} /></button>}
                        <button onClick={() => deleteReply(r.id)} className="p-1 rounded hover:bg-red-50 text-red-300"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                  {topicReplies[t.id].length === 0 && <p className="text-xs text-gray-400 text-center py-2">Aucune réponse</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default ForumModerationPage;
